import { useCallback, useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/router";
import { mutate } from "swr";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";

export default function DeleteUserModalForm({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [prevOpen, setPrevOpen] = useState(false);
  if (open && !prevOpen) {
    setError(null);
    setReason("");
  }
  if (open !== prevOpen) {
    setPrevOpen(open);
  }

  const router = useRouter();
  const deleteUserClicked = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (processing) return;

      setProcessing(true);
      setError(null);

      fetch("/web_api/users/me", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason }),
      })
        .then((resp) => resp.json())
        .then((json) => {
          if (json.status === "ok") {
            router.push("/").then(() => mutate("/web_api/accounts/current"));
          } else {
            setError(json.error || "Error. Try again later.");
          }
        })
        .catch((e) => {
          setError(e.toString() || "Error. Try again later.");
        })
        .finally(() => {
          setProcessing(false);
        });
    },
    [processing, reason, router],
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} size="lg">
      <DialogHeader>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/20">
            <ExclamationTriangleIcon className="h-6 w-6 text-destructive" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-destructive">Delete user account</DialogTitle>
            <DialogDescription className="mt-1">
              Are you sure you want to delete your user account? Your workspace, all monitors,
              contacts, and data will be permanently removed. You will also be removed from any
              teams you belong to. This action cannot be undone.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={deleteUserClicked}>
        <DialogBody>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reason (optional)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please leave some feedback about why you are leaving. It will help us improve our service. Thank you!"
              rows={4}
              className="w-full"
            />
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" disabled={processing}>
            {processing ? "Deleting..." : "Delete user account"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
